import React, { useState, useEffect } from 'react';
import { Activity, RefreshCw, AlertTriangle, CheckCircle, Clock, XCircle, ChevronDown, ChevronRight, ExternalLink, Package, X, ImageOff, HelpCircle, Plus, Database } from 'lucide-react';
import axios from 'axios';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import config from '../config';

const API_URL = config.API_URL;

const FeedDiagnosticsTool = ({ token, selectedClientId }) => {
  // États
  const [allAccounts, setAllAccounts] = useState([]); // Tous les comptes non filtrés
  const [accounts, setAccounts] = useState([]); // Comptes filtrés par client
  const [selectedAccount, setSelectedAccount] = useState('');
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedIssue, setExpandedIssue] = useState(null);
  const [severityFilter, setSeverityFilter] = useState('all');

  // États pour le modal des produits rejetés
  const [showRejectedModal, setShowRejectedModal] = useState(false);
  const [rejectedProducts, setRejectedProducts] = useState([]);
  const [rejectedTotal, setRejectedTotal] = useState(0);
  const [loadingRejected, setLoadingRejected] = useState(false);
  const [rejectedOffset, setRejectedOffset] = useState(0);
  const [hasMoreRejected, setHasMoreRejected] = useState(false);

  // États pour la vue synthèse (tous les flux)
  const [allFeedsHealth, setAllFeedsHealth] = useState([]);
  const [loadingAllFeeds, setLoadingAllFeeds] = useState(false);
  const [selectedAccountForModal, setSelectedAccountForModal] = useState(null);

  // États pour les flux du compte sélectionné
  const [selectedAccountFeeds, setSelectedAccountFeeds] = useState([]);
  const [selectedFeedLabel, setSelectedFeedLabel] = useState(null); // Flux spécifique sélectionné

  // État pour le tooltip du score
  const [showScoreTooltip, setShowScoreTooltip] = useState(false);
  const [showScoreTooltipDetail, setShowScoreTooltipDetail] = useState(false);

  // État pour l'ajout de compte Merchant
  const [addingMerchant, setAddingMerchant] = useState(null); // merchantId en cours d'ajout

  // Fonction pour ajouter un compte Merchant à la bibliothèque
  const addMerchantToLibrary = async (merchantId, merchantName) => {
    try {
      setAddingMerchant(merchantId);
      console.log(`➕ [FEED-DIAGNOSTICS] Ajout du Merchant ${merchantId} à la bibliothèque...`);

      const response = await axios.post(
        `${API_URL}/api/tools/feed-diagnostics/add-merchant`,
        { merchantId, merchantName },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        console.log(`✅ [FEED-DIAGNOSTICS] Merchant ${merchantId} ajouté avec succès`);

        // Rafraîchir la liste des comptes
        const accountsResponse = await axios.get(`${API_URL}/api/tools/feed-diagnostics/accounts`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (accountsResponse.data.success) {
          const fetchedAccounts = accountsResponse.data.data.accounts;
          setAllAccounts(fetchedAccounts);

          // Re-filtrer selon le client sélectionné
          const filtered = await filterAccountsByClient(fetchedAccounts, selectedClientId);
          // Trier les comptes par ordre alphabétique
          const sortedAccounts = [...filtered].sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' })
          );
          setAccounts(sortedAccounts);

          // Sélectionner le compte ajouté
          setSelectedAccount(merchantId);
        }
      }
    } catch (err) {
      console.error('❌ [FEED-DIAGNOSTICS] Erreur ajout Merchant:', err);
      const errorMessage = err.response?.data?.error || 'Erreur lors de l\'ajout du compte';
      setError(errorMessage);
    } finally {
      setAddingMerchant(null);
    }
  };

  // Filtrer les comptes selon le client sélectionné
  // Utilise l'API Google Ads pour trouver les Merchant Center liés aux comptes Google Ads du client
  const filterAccountsByClient = async (accountsList, clientId) => {
    if (!clientId) {
      return accountsList; // Pas de filtrage si aucun client sélectionné
    }

    try {
      // Récupérer tous les sites et filtrer par clientId
      const sitesResponse = await axios.get(`${API_URL}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (sitesResponse.data.success && sitesResponse.data.sites) {
        const clientSites = sitesResponse.data.sites.filter(site => site.clientId === clientId);
        const authorizedAccountIds = clientSites.flatMap(site => site.accountIds || []);

        console.log('🎯 [FILTER FEED-DIAGNOSTICS] Sites du client:', clientSites.length);
        console.log('🎯 [FILTER FEED-DIAGNOSTICS] IDs comptes autorisés:', authorizedAccountIds);

        if (authorizedAccountIds.length === 0) {
          console.log('🎯 [FILTER FEED-DIAGNOSTICS] Aucun compte associé au client');
          return [];
        }

        // Filtrer pour ne garder que les IDs Google Ads (format XXX-XXX-XXXX)
        const googleAdsIds = authorizedAccountIds.filter(id => /^\d{3}-\d{3}-\d{4}$/.test(id));
        console.log('🎯 [FILTER FEED-DIAGNOSTICS] Comptes Google Ads identifiés:', googleAdsIds);

        if (googleAdsIds.length === 0) {
          // Pas de compte Google Ads -> pas de liaison possible -> retourner liste vide
          console.log('🎯 [FILTER FEED-DIAGNOSTICS] Aucun compte Google Ads -> pas de Merchant lié');
          return [];
        }

        // Appeler l'API pour récupérer les Merchant Center liés à ces comptes Google Ads
        const linkedResponse = await axios.post(
          `${API_URL}/api/tools/feed-diagnostics/linked-merchants`,
          { googleAdsAccountIds: googleAdsIds },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (linkedResponse.data.success && linkedResponse.data.data?.linkedMerchants) {
          const linkedMerchants = linkedResponse.data.data.linkedMerchants;
          const linkedMerchantIds = linkedMerchants.map(m => m.merchantId);
          console.log('🎯 [FILTER FEED-DIAGNOSTICS] Merchant Center liés:', linkedMerchantIds);

          // Filtrer les comptes Merchant pour ne garder que ceux liés
          const filteredAccounts = accountsList.filter(account => {
            const merchantId = String(account.merchantId || '');
            const isLinked = linkedMerchantIds.includes(merchantId);
            console.log(`🔍 [FILTER] Compte ${account.name}: merchantId=${merchantId}, isLinked=${isLinked}`);
            return isLinked;
          });

          // Si des Merchant liés ne sont pas dans la bibliothèque, les ajouter comme comptes virtuels
          const existingMerchantIds = filteredAccounts.map(a => String(a.merchantId));
          const missingMerchants = linkedMerchants.filter(m => !existingMerchantIds.includes(m.merchantId));

          if (missingMerchants.length > 0) {
            console.log('🎯 [FILTER FEED-DIAGNOSTICS] Merchant liés non trouvés dans la bibliothèque, ajout comme comptes virtuels:', missingMerchants);
            missingMerchants.forEach(m => {
              filteredAccounts.push({
                id: `virtual-${m.merchantId}`,
                name: m.merchantName || `Merchant Center ${m.merchantId}`,
                merchantId: m.merchantId,
                hasConnection: true, // On suppose qu'il y a une connexion via Google Ads
                isVirtual: true,
                linkedFromGoogleAds: m.linkedFromGoogleAds
              });
            });
          }

          console.log('🎯 [FILTER FEED-DIAGNOSTICS] Comptes filtrés (avec virtuels):', filteredAccounts.length, '/', accountsList.length);
          return filteredAccounts;
        }
      }
    } catch (error) {
      console.error('❌ [FILTER FEED-DIAGNOSTICS] Erreur filtrage comptes:', error);
    }

    return accountsList; // Retourner tous les comptes en cas d'erreur
  };

  // Charger les comptes Merchant API
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setLoadingAccounts(true);
        const response = await axios.get(`${API_URL}/api/tools/feed-diagnostics/accounts`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          const fetchedAccounts = response.data.data.accounts;
          setAllAccounts(fetchedAccounts);

          // Filtrer selon le client sélectionné
          const filtered = await filterAccountsByClient(fetchedAccounts, selectedClientId);
          // Trier les comptes par ordre alphabétique
          const sortedAccounts = [...filtered].sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' })
          );
          setAccounts(sortedAccounts);

          // Auto-sélectionner si un seul compte ET un client est sélectionné
          if (sortedAccounts.length === 1 && selectedClientId) {
            setSelectedAccount(sortedAccounts[0].merchantId);
          }
        }
      } catch (err) {
        console.error('Erreur chargement comptes:', err);
        setError('Impossible de charger les comptes Merchant API');
      } finally {
        setLoadingAccounts(false);
      }
    };

    if (token) {
      fetchAccounts();
    }
  }, [token]);

  // Re-filtrer les comptes quand le client sélectionné change
  useEffect(() => {
    const applyFilter = async () => {
      if (allAccounts.length > 0) {
        console.log('🎯 [FEED-DIAGNOSTICS] selectedClientId changé:', selectedClientId);
        setLoadingAccounts(true);
        const filtered = await filterAccountsByClient(allAccounts, selectedClientId);
        // Trier les comptes par ordre alphabétique
        const sortedAccounts = [...filtered].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' })
        );
        setAccounts(sortedAccounts);

        // Auto-sélectionner si un seul compte ET un client est sélectionné
        if (sortedAccounts.length === 1 && selectedClientId) {
          setSelectedAccount(sortedAccounts[0].merchantId);
        } else {
          setSelectedAccount(''); // Réinitialiser la sélection si plusieurs comptes ou aucun client
        }

        setHealthData(null);
        setAllFeedsHealth([]);
        setLoadingAccounts(false);
      }
    };

    applyFilter();
  }, [selectedClientId, allAccounts]);

  // Charger les données de santé quand un compte est sélectionné
  useEffect(() => {
    if (selectedAccount && selectedAccount !== '') {
      setSelectedFeedLabel(null); // Réinitialiser le flux sélectionné
      fetchAccountFeeds();
      setAllFeedsHealth([]);
    } else if (accounts.length > 0 && selectedClientId) {
      // Charger la synthèse de tous les flux SEULEMENT si un client est sélectionné
      setHealthData(null);
      setSelectedAccountFeeds([]);
      setSelectedFeedLabel(null);
      fetchAllFeedsHealth();
    } else {
      // Pas de compte ni de client sélectionné - ne rien charger
      setHealthData(null);
      setSelectedAccountFeeds([]);
      setSelectedFeedLabel(null);
      setAllFeedsHealth([]);
    }
  }, [selectedAccount, accounts, selectedClientId]);

  // Charger les données de santé pour tous les flux
  const fetchAllFeedsHealth = async () => {
    if (accounts.length === 0) return;

    try {
      setLoadingAllFeeds(true);
      setError(null);

      // Appeler le nouvel endpoint qui retourne les stats par flux
      const response = await axios.post(
        `${API_URL}/api/tools/feed-diagnostics/feeds-health`,
        { accountIds: accounts.map(a => ({ merchantId: a.merchantId, name: a.name })) },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setAllFeedsHealth(response.data.data.feeds);
      } else {
        setError('Erreur lors du chargement des flux');
      }
    } catch (err) {
      console.error('Erreur chargement synthèse flux:', err);
      setError('Erreur lors du chargement de la synthèse');
    } finally {
      setLoadingAllFeeds(false);
    }
  };

  // Récupérer les flux d'un compte sélectionné
  const fetchAccountFeeds = async () => {
    if (!selectedAccount) return;

    try {
      setLoading(true);
      setError(null);
      setSelectedAccountFeeds([]);
      setHealthData(null);

      const selectedAccountInfo = accounts.find(a => a.merchantId === selectedAccount);

      const feedsResponse = await axios.post(
        `${API_URL}/api/tools/feed-diagnostics/feeds-health`,
        { accountIds: [{ merchantId: selectedAccount, name: selectedAccountInfo?.name || selectedAccount }] },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (feedsResponse.data.success) {
        const feeds = feedsResponse.data.data.feeds || [];
        setSelectedAccountFeeds(feeds);

        // Si un seul flux, sélectionner automatiquement et charger la vue détaillée
        if (feeds.length === 1) {
          setSelectedFeedLabel(feeds[0].feedLabel);
        }
      }
    } catch (err) {
      console.error('Erreur chargement flux:', err);
      setError(err.response?.data?.error || 'Erreur lors du chargement des flux');
    } finally {
      setLoading(false);
    }
  };

  // Charger les données de santé détaillées pour un flux spécifique
  const fetchFeedHealthData = async (feedLabel) => {
    if (!selectedAccount) return;

    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(
        `${API_URL}/api/tools/feed-diagnostics/health`,
        { merchantId: selectedAccount },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Récupérer les stats du flux spécifique depuis selectedAccountFeeds
        const feedStats = selectedAccountFeeds.find(f => f.feedLabel === feedLabel);

        // Modifier les données de santé pour utiliser les stats du flux
        const healthData = response.data.data;
        if (feedStats) {
          healthData.health.statuses = feedStats.statuses;
          healthData.health.healthScore = feedStats.healthScore;
        }

        setHealthData(healthData);
      }
    } catch (err) {
      console.error('Erreur chargement santé flux:', err);
      setError(err.response?.data?.error || 'Erreur lors de l\'analyse du flux');
    } finally {
      setLoading(false);
    }
  };

  // Charger la vue détaillée quand un flux est sélectionné
  useEffect(() => {
    if (selectedFeedLabel && selectedAccount) {
      fetchFeedHealthData(selectedFeedLabel);
    }
  }, [selectedFeedLabel]);

  // Charger les produits rejetés
  const fetchRejectedProducts = async (offset = 0, accountId = null) => {
    const targetAccount = accountId || selectedAccountForModal || selectedAccount;
    if (!targetAccount) return;

    try {
      setLoadingRejected(true);

      const response = await axios.post(
        `${API_URL}/api/tools/feed-diagnostics/rejected-products`,
        { merchantId: targetAccount, limit: 50, offset },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const data = response.data.data;
        if (offset === 0) {
          setRejectedProducts(data.products);
        } else {
          setRejectedProducts(prev => [...prev, ...data.products]);
        }
        setRejectedTotal(data.total);
        setRejectedOffset(offset);
        setHasMoreRejected(data.hasMore);
      }
    } catch (err) {
      console.error('Erreur chargement produits rejetés:', err);
    } finally {
      setLoadingRejected(false);
    }
  };

  // Ouvrir le modal des produits rejetés
  const handleOpenRejectedModal = (accountId = null) => {
    const targetAccount = accountId || selectedAccount;
    setSelectedAccountForModal(targetAccount);
    setShowRejectedModal(true);
    setRejectedProducts([]);
    setRejectedOffset(0);
    fetchRejectedProducts(0, targetAccount);
  };

  // Charger plus de produits
  const loadMoreRejected = () => {
    fetchRejectedProducts(rejectedOffset + 50, selectedAccountForModal);
  };

  // Couleurs pour le graphique
  const COLORS = {
    approved: '#22c55e',
    limited: '#f59e0b',
    pending: '#6b7280',
    disapproved: '#ef4444'
  };

  // Données pour le graphique donut
  const chartData = healthData?.health?.statuses ? [
    { name: 'Approuvés', value: healthData.health.statuses.approved, color: COLORS.approved },
    { name: 'Limités', value: healthData.health.statuses.limited, color: COLORS.limited },
    { name: 'En attente', value: healthData.health.statuses.pending, color: COLORS.pending },
    { name: 'Rejetés', value: healthData.health.statuses.disapproved, color: COLORS.disapproved }
  ].filter(item => item.value > 0) : [];

  // Filtrer les issues par sévérité
  const filteredIssues = healthData?.health?.issues?.filter(issue => {
    if (severityFilter === 'all') return true;
    return issue.severity === severityFilter;
  }) || [];

  // Icône de sévérité
  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <XCircle size={16} className="text-red-600" />;
      case 'error':
        return <AlertTriangle size={16} className="text-red-500" />;
      case 'warning':
      case 'suggestion':
        return <AlertTriangle size={16} className="text-yellow-500" />;
      default:
        return <AlertTriangle size={16} className="text-gray-500" />;
    }
  };

  // Badge de sévérité
  const getSeverityBadge = (severity) => {
    const styles = {
      critical: 'bg-red-100 text-red-800 border-red-200',
      error: 'bg-red-50 text-red-700 border-red-100',
      warning: 'bg-yellow-50 text-yellow-700 border-yellow-100',
      suggestion: 'bg-blue-50 text-blue-700 border-blue-100'
    };
    const labels = {
      critical: 'Critique',
      error: 'Erreur',
      warning: 'Avertissement',
      suggestion: 'Suggestion'
    };
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded border ${styles[severity] || styles.warning}`}>
        {labels[severity] || severity}
      </span>
    );
  };

  // Couleur du score de santé
  const getScoreColor = (grade) => {
    switch (grade) {
      case 'A': return '#22c55e';
      case 'B': return '#84cc16';
      case 'C': return '#f59e0b';
      case 'D': return '#f97316';
      case 'F': return '#ef4444';
      default: return '#6b7280';
    }
  };

  return (
    <div className="feed-diagnostics-tool" style={{ width: '100%', padding: '0' }}>
      {/* Animation CSS pour le spinner */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* En-tête */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#111827',
              display: 'flex',
              alignItems: 'center',
              margin: '0 0 8px 0'
            }}>
              <Activity style={{ marginRight: '8px', height: '24px', width: '24px', color: '#10b981' }} />
              Diagnostic du Flux
            </h2>
            <p style={{ color: '#6b7280', margin: '0', fontSize: '16px' }}>
              Analysez la santé de vos flux produits Google Merchant Center en temps réel
            </p>
          </div>
        </div>
      </div>

      {/* Section Compte Google Merchant Center */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <h3 style={{ fontSize: '16px', fontWeight: '500', color: '#111827', margin: '0 0 12px 0' }}>
          Compte Google Merchant Center
        </h3>

        {loadingAccounts ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <div style={{
              display: 'inline-block',
              width: '20px',
              height: '20px',
              border: '2px solid #f3f3f3',
              borderTop: '2px solid #6366f1',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ margin: '8px 0 0 0', color: '#6b7280' }}>Chargement des comptes...</p>
          </div>
        ) : accounts.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
            <Package size={32} style={{ margin: '0 auto 8px', color: '#9ca3af' }} />
            <p style={{ margin: 0 }}>Aucun compte Merchant API trouvé</p>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px' }}>
              Connectez un compte dans "Mes connecteurs"
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151', minWidth: '100px' }}>
              Sélectionner :
            </label>
            <select
              value={selectedAccount}
              onChange={(e) => setSelectedAccount(e.target.value)}
              style={{
                flex: '1',
                minWidth: '300px',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                background: 'white'
              }}
            >
              <option value="">-- Choisir un compte --</option>
              {accounts.map(account => {
                // Éviter le doublon si le nom contient déjà l'ID
                const nameIncludesId = account.name && account.name.includes(`(${account.merchantId})`);
                const displayName = nameIncludesId ? account.name : `${account.name} (${account.merchantId})`;
                return (
                  <option key={account.merchantId} value={account.merchantId}>
                    {account.isVirtual ? '⚠️ ' : ''}{displayName}{account.isVirtual ? ' - Non enregistré' : ''}
                  </option>
                );
              })}
            </select>

            {/* Bouton rafraîchir */}
            <button
              onClick={fetchAccountFeeds}
              disabled={loading || !selectedAccount}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                backgroundColor: '#6366f1',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: (loading || !selectedAccount) ? 'not-allowed' : 'pointer',
                opacity: (loading || !selectedAccount) ? 0.7 : 1
              }}
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Analyser
            </button>
          </div>
        )}

        {/* Message pour compte virtuel avec bouton d'ajout */}
        {selectedAccount && accounts.find(a => a.merchantId === selectedAccount)?.isVirtual && (
          <div style={{
            marginTop: '12px',
            padding: '12px 16px',
            backgroundColor: '#fef3c7',
            border: '1px solid #fcd34d',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Database size={20} style={{ color: '#d97706' }} />
              <div>
                <p style={{ margin: 0, fontSize: '14px', fontWeight: '500', color: '#92400e' }}>
                  Compte non enregistré
                </p>
                <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: '#a16207' }}>
                  Ce compte Merchant Center n'est pas dans votre bibliothèque. Ajoutez-le pour y accéder facilement.
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                const account = accounts.find(a => a.merchantId === selectedAccount);
                if (account) {
                  addMerchantToLibrary(account.merchantId, account.name);
                }
              }}
              disabled={addingMerchant === selectedAccount}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                backgroundColor: '#059669',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: addingMerchant === selectedAccount ? 'not-allowed' : 'pointer',
                opacity: addingMerchant === selectedAccount ? 0.7 : 1,
                fontSize: '13px',
                fontWeight: '500',
                whiteSpace: 'nowrap'
              }}
            >
              {addingMerchant === selectedAccount ? (
                <>
                  <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
                  Ajout...
                </>
              ) : (
                <>
                  <Plus size={14} />
                  Ajouter à Mes Données
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Erreur */}
      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#dc2626',
          marginBottom: '20px'
        }}>
          <strong>Erreur :</strong> {error}
        </div>
      )}

      {/* Chargement */}
      {loading && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #f3f3f3',
            borderTop: '3px solid #6366f1',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '16px'
          }}></div>
          <p style={{ color: '#6b7280', margin: 0 }}>Analyse du flux en cours...</p>
        </div>
      )}

      {/* Tableau des flux du compte sélectionné (si plusieurs flux et pas de flux spécifique sélectionné) */}
      {!loading && selectedAccount && selectedAccountFeeds.length > 1 && !selectedFeedLabel && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb'
          }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>
              Flux du compte ({selectedAccountFeeds.length} flux)
            </h3>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Flux</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      Score
                      <div
                        style={{ position: 'relative', display: 'inline-block' }}
                        onMouseEnter={() => setShowScoreTooltip(true)}
                        onMouseLeave={() => setShowScoreTooltip(false)}
                      >
                        <HelpCircle size={14} style={{ color: '#9ca3af', cursor: 'help' }} />
                        {showScoreTooltip && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            marginTop: '8px',
                            padding: '12px',
                            backgroundColor: '#1f2937',
                            color: 'white',
                            borderRadius: '8px',
                            fontSize: '12px',
                            width: '280px',
                            textAlign: 'left',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                            zIndex: 1000,
                            lineHeight: '1.5'
                          }}>
                            <div style={{ fontWeight: '600', marginBottom: '8px' }}>Calcul du Score</div>
                            <div style={{ marginBottom: '6px' }}>
                              <strong>50%</strong> = % produits approuvés
                            </div>
                            <div style={{ marginBottom: '8px' }}>
                              <strong>50%</strong> = Base - pénalités (rejetés & limités)
                            </div>
                            <div style={{ borderTop: '1px solid #374151', paddingTop: '8px', fontSize: '11px' }}>
                              A: 90+ | B: 75-89 | C: 60-74 | D: 40-59 | F: &lt;40
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Approuvés</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Limités</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>En attente</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Rejetés</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {selectedAccountFeeds.map((feed, index) => (
                  <tr
                    key={`${feed.merchantId}-${feed.feedLabel}`}
                    style={{
                      backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb',
                      cursor: 'pointer'
                    }}
                    onClick={() => setSelectedFeedLabel(feed.feedLabel)}
                  >
                    {/* Flux */}
                    <td style={{ padding: '16px', borderBottom: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 10px',
                          backgroundColor: '#e0e7ff',
                          color: '#4f46e5',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '600'
                        }}>
                          {feed.feedLabel}
                        </span>
                        {feed.feedName !== feed.feedLabel && (
                          <span style={{ color: '#6b7280', fontSize: '13px' }}>{feed.feedName}</span>
                        )}
                      </div>
                    </td>

                    {/* Score */}
                    <td style={{ padding: '16px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>
                      <div>
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: getScoreColor(feed.healthScore?.grade) + '15',
                          color: getScoreColor(feed.healthScore?.grade),
                          fontSize: '18px',
                          fontWeight: '700'
                        }}>
                          {feed.healthScore?.grade || '-'}
                        </div>
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                          {feed.healthScore?.score}/100
                        </div>
                      </div>
                    </td>

                    {/* Approuvés */}
                    <td style={{ padding: '16px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>
                      <div>
                        <div style={{ fontSize: '20px', fontWeight: '600', color: '#22c55e' }}>
                          {feed.statuses?.percentApproved || 0}%
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          {feed.statuses?.approved?.toLocaleString() || 0} produits
                        </div>
                      </div>
                    </td>

                    {/* Limités */}
                    <td style={{ padding: '16px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>
                      <div>
                        <div style={{ fontSize: '20px', fontWeight: '600', color: '#f59e0b' }}>
                          {feed.statuses?.percentLimited || 0}%
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          {feed.statuses?.limited?.toLocaleString() || 0} produits
                        </div>
                      </div>
                    </td>

                    {/* En attente */}
                    <td style={{ padding: '16px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>
                      <div>
                        <div style={{ fontSize: '20px', fontWeight: '600', color: '#6b7280' }}>
                          {feed.statuses?.percentPending || 0}%
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          {feed.statuses?.pending?.toLocaleString() || 0} produits
                        </div>
                      </div>
                    </td>

                    {/* Rejetés */}
                    <td style={{ padding: '16px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>
                      <div>
                        <div style={{ fontSize: '20px', fontWeight: '600', color: '#ef4444' }}>
                          {feed.statuses?.percentDisapproved || 0}%
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          {feed.statuses?.disapproved?.toLocaleString() || 0} produits
                        </div>
                        {feed.statuses?.disapproved > 0 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenRejectedModal(feed.merchantId);
                            }}
                            style={{
                              marginTop: '4px',
                              padding: '4px 8px',
                              fontSize: '11px',
                              color: '#ef4444',
                              backgroundColor: '#fef2f2',
                              border: '1px solid #fecaca',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            Voir détails →
                          </button>
                        )}
                      </div>
                    </td>

                    {/* Total */}
                    <td style={{ padding: '16px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>
                      <div>
                        <div style={{ fontSize: '20px', fontWeight: '600', color: '#374151' }}>
                          {feed.statuses?.total?.toLocaleString() || 0}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          produits
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Contenu principal - Vue détaillée d'un flux */}
      {!loading && healthData && selectedFeedLabel && (
        <>
          {/* Bouton retour si plusieurs flux */}
          {selectedAccountFeeds.length > 1 && (
            <button
              onClick={() => setSelectedFeedLabel(null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                marginBottom: '16px',
                backgroundColor: '#f3f4f6',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#374151'
              }}
            >
              ← Retour aux flux
            </button>
          )}

          {/* Titre du flux sélectionné */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px'
          }}>
            <span style={{
              padding: '6px 14px',
              backgroundColor: '#e0e7ff',
              color: '#4f46e5',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600'
            }}>
              {selectedFeedLabel}
            </span>
            <span style={{ color: '#6b7280', fontSize: '14px' }}>
              {selectedAccountFeeds.find(f => f.feedLabel === selectedFeedLabel)?.feedName !== selectedFeedLabel
                ? selectedAccountFeeds.find(f => f.feedLabel === selectedFeedLabel)?.feedName
                : ''}
            </span>
          </div>

          {/* KPIs */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            {/* Score de santé */}
            <div style={{
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '48px',
                fontWeight: '700',
                color: getScoreColor(healthData.health.healthScore?.grade)
              }}>
                {healthData.health.healthScore?.grade || '-'}
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                Score: {healthData.health.healthScore?.score}/100
                <div
                  style={{ position: 'relative', display: 'inline-block' }}
                  onMouseEnter={() => setShowScoreTooltipDetail(true)}
                  onMouseLeave={() => setShowScoreTooltipDetail(false)}
                >
                  <HelpCircle size={14} style={{ color: '#9ca3af', cursor: 'help' }} />
                  {showScoreTooltipDetail && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      marginTop: '8px',
                      padding: '12px',
                      backgroundColor: '#1f2937',
                      color: 'white',
                      borderRadius: '8px',
                      fontSize: '12px',
                      width: '280px',
                      textAlign: 'left',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                      zIndex: 1000,
                      lineHeight: '1.5'
                    }}>
                      <div style={{ fontWeight: '600', marginBottom: '8px' }}>Calcul du Score</div>
                      <div style={{ marginBottom: '6px' }}>
                        <strong>50%</strong> = % produits approuvés
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        <strong>50%</strong> = Base - pénalités (critiques & erreurs)
                      </div>
                      <div style={{ borderTop: '1px solid #374151', paddingTop: '8px', fontSize: '11px' }}>
                        A: 90+ | B: 75-89 | C: 60-74 | D: 40-59 | F: &lt;40
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                {healthData.health.healthScore?.description}
              </div>
            </div>

            {/* Approuvés */}
            <div style={{
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <CheckCircle size={20} style={{ color: '#22c55e' }} />
                <span style={{ fontSize: '14px', color: '#6b7280' }}>Approuvés</span>
              </div>
              <div style={{ fontSize: '32px', fontWeight: '600', color: '#22c55e' }}>
                {healthData.health.statuses?.percentApproved || 0}%
              </div>
              <div style={{ fontSize: '14px', color: '#9ca3af' }}>
                {healthData.health.statuses?.approved?.toLocaleString() || 0} produits
              </div>
            </div>

            {/* Limités */}
            <div style={{
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <AlertTriangle size={20} style={{ color: '#f59e0b' }} />
                <span style={{ fontSize: '14px', color: '#6b7280' }}>Limités</span>
              </div>
              <div style={{ fontSize: '32px', fontWeight: '600', color: '#f59e0b' }}>
                {healthData.health.statuses?.percentLimited || 0}%
              </div>
              <div style={{ fontSize: '14px', color: '#9ca3af' }}>
                {healthData.health.statuses?.limited?.toLocaleString() || 0} produits
              </div>
            </div>

            {/* En attente */}
            <div style={{
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Clock size={20} style={{ color: '#f59e0b' }} />
                <span style={{ fontSize: '14px', color: '#6b7280' }}>En attente</span>
              </div>
              <div style={{ fontSize: '32px', fontWeight: '600', color: '#f59e0b' }}>
                {healthData.health.statuses?.percentPending || 0}%
              </div>
              <div style={{ fontSize: '14px', color: '#9ca3af' }}>
                {healthData.health.statuses?.pending?.toLocaleString() || 0} produits
              </div>
            </div>

            {/* Rejetés - Cliquable */}
            <div
              onClick={healthData.health.statuses?.disapproved > 0 ? () => handleOpenRejectedModal() : undefined}
              style={{
                padding: '20px',
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: '1px solid #e5e7eb',
                cursor: healthData.health.statuses?.disapproved > 0 ? 'pointer' : 'default',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (healthData.health.statuses?.disapproved > 0) {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.2)';
                  e.currentTarget.style.borderColor = '#ef4444';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                e.currentTarget.style.borderColor = '#e5e7eb';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <XCircle size={20} style={{ color: '#ef4444' }} />
                <span style={{ fontSize: '14px', color: '#6b7280' }}>Rejetés</span>
                {healthData.health.statuses?.disapproved > 0 && (
                  <span style={{ fontSize: '11px', color: '#ef4444', marginLeft: 'auto' }}>Voir détails →</span>
                )}
              </div>
              <div style={{ fontSize: '32px', fontWeight: '600', color: '#ef4444' }}>
                {healthData.health.statuses?.percentDisapproved || 0}%
              </div>
              <div style={{ fontSize: '14px', color: '#9ca3af' }}>
                {healthData.health.statuses?.disapproved?.toLocaleString() || 0} produits
              </div>
            </div>

            {/* Total */}
            <div style={{
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Package size={20} style={{ color: '#6b7280' }} />
                <span style={{ fontSize: '14px', color: '#6b7280' }}>Total</span>
              </div>
              <div style={{ fontSize: '32px', fontWeight: '600', color: '#374151' }}>
                {healthData.health.statuses?.total?.toLocaleString() || 0}
              </div>
              <div style={{ fontSize: '14px', color: '#9ca3af' }}>
                produits analysés
              </div>
            </div>
          </div>

          {/* Graphique et Problèmes */}
          <div style={{ display: 'grid', gridTemplateColumns: '350px 1fr', gap: '24px' }}>
            {/* Graphique Donut */}
            <div style={{
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                Répartition des statuts
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [`${value.toLocaleString()} produits`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '12px' }}>
                {chartData.map((item, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: item.color }} />
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>{item.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Liste des problèmes */}
            <div style={{
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>
                  Problèmes détectés ({healthData.health.summary?.uniqueIssueTypes || 0})
                </h3>
                <select
                  value={severityFilter}
                  onChange={(e) => setSeverityFilter(e.target.value)}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '13px'
                  }}
                >
                  <option value="all">Tous les niveaux</option>
                  <option value="critical">Critique</option>
                  <option value="error">Erreur</option>
                  <option value="warning">Avertissement</option>
                </select>
              </div>

              <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {filteredIssues.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af' }}>
                    <CheckCircle size={32} style={{ marginBottom: '8px', color: '#22c55e' }} />
                    <p>Aucun problème détecté</p>
                  </div>
                ) : (
                  filteredIssues.map((issue, index) => (
                    <div
                      key={index}
                      style={{
                        padding: '12px',
                        borderBottom: index < filteredIssues.length - 1 ? '1px solid #f3f4f6' : 'none',
                        cursor: 'pointer'
                      }}
                      onClick={() => setExpandedIssue(expandedIssue === index ? null : index)}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                        <div style={{ marginTop: '2px' }}>
                          {getSeverityIcon(issue.severity)}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <span style={{ fontWeight: '500', color: '#374151' }}>
                              {issue.messageFr || issue.description}
                            </span>
                          </div>
                          <div style={{ fontSize: '13px', color: '#6b7280' }}>
                            {issue.count} produits affectés
                          </div>

                          {/* Détails étendus */}
                          {expandedIssue === index && (
                            <div style={{
                              marginTop: '12px',
                              padding: '12px',
                              backgroundColor: '#f9fafb',
                              borderRadius: '8px'
                            }}>
                              <div style={{ marginBottom: '8px' }}>
                                <strong style={{ fontSize: '13px', color: '#374151' }}>Action corrective :</strong>
                                <p style={{ fontSize: '13px', color: '#6b7280', margin: '4px 0 0 0' }}>
                                  {issue.actionCorrective}
                                </p>
                              </div>
                              {issue.documentation && (
                                <a
                                  href={issue.documentation}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '13px',
                                    color: '#6366f1',
                                    textDecoration: 'none'
                                  }}
                                >
                                  <ExternalLink size={14} />
                                  Voir la documentation
                                </a>
                              )}
                              {issue.affectedProducts?.length > 0 && (
                                <div style={{ marginTop: '8px' }}>
                                  <strong style={{ fontSize: '13px', color: '#374151' }}>Exemples de produits :</strong>
                                  <ul style={{ margin: '4px 0 0 0', paddingLeft: '16px' }}>
                                    {issue.affectedProducts.slice(0, 5).map((product, pIndex) => (
                                      <li key={pIndex} style={{ fontSize: '12px', color: '#6b7280' }}>
                                        {product.title || product.productId}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div style={{ marginTop: '2px' }}>
                          {expandedIssue === index ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Dernière mise à jour */}
          <div style={{ marginTop: '16px', textAlign: 'right', fontSize: '12px', color: '#9ca3af' }}>
            Dernière analyse : {new Date(healthData.generatedAt).toLocaleString('fr-FR')}
          </div>
        </>
      )}

      {/* Modal des produits rejetés */}
      {showRejectedModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => setShowRejectedModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '900px',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header du modal */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <XCircle size={24} style={{ color: '#ef4444' }} />
                <div>
                  <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                    Produits Rejetés
                    {selectedAccountForModal && (
                      <span style={{ fontWeight: '400', color: '#6b7280', fontSize: '14px', marginLeft: '8px' }}>
                        — {accounts.find(a => a.merchantId === selectedAccountForModal)?.name || selectedAccountForModal}
                      </span>
                    )}
                  </h2>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                    {rejectedTotal} produit{rejectedTotal > 1 ? 's' : ''} rejeté{rejectedTotal > 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowRejectedModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <X size={24} style={{ color: '#6b7280' }} />
              </button>
            </div>

            {/* Contenu du modal */}
            <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px' }}>
              {loadingRejected && rejectedProducts.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    border: '3px solid #f3f3f3',
                    borderTop: '3px solid #ef4444',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 16px'
                  }}></div>
                  <p style={{ color: '#6b7280', margin: 0 }}>Chargement des produits rejetés...</p>
                </div>
              ) : rejectedProducts.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                  <CheckCircle size={48} style={{ color: '#22c55e', marginBottom: '12px' }} />
                  <p>Aucun produit rejeté</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {rejectedProducts.map((product, index) => {
                    // Dédupliquer les raisons par code
                    const uniqueIssues = product.issues.reduce((acc, issue) => {
                      const key = issue.code || issue.messageFr;
                      if (!acc.find(i => (i.code || i.messageFr) === key)) {
                        acc.push(issue);
                      }
                      return acc;
                    }, []);

                    // Construire l'URL Google Merchant Center
                    // Format productId: "online:fr:FR:SKU123" -> channel:language:feedLabel:offerId
                    const productParts = product.productId?.split(':') || [];
                    const channel = productParts[0] === 'online' ? '0' : '1'; // 0=online, 1=local
                    const language = productParts[1] || 'fr';
                    const feedLabel = productParts[2] || 'FR';
                    const offerId = productParts.slice(3).join(':') || product.productId; // Rejoindre au cas où l'offerId contient des ":"
                    const accountForUrl = selectedAccountForModal || selectedAccount;
                    const merchantCenterUrl = `https://merchants.google.com/mc/items/details?a=${accountForUrl}&offerId=${encodeURIComponent(offerId)}&language=${language}&channel=${channel}&feedLabel=${feedLabel}&tab=issues`;

                    return (
                      <div
                        key={index}
                        style={{
                          padding: '16px',
                          backgroundColor: '#fef2f2',
                          border: '1px solid #fecaca',
                          borderRadius: '12px'
                        }}
                      >
                        <div style={{ display: 'flex', gap: '16px' }}>
                          {/* Image produit */}
                          <div style={{
                            width: '70px',
                            height: '70px',
                            borderRadius: '8px',
                            backgroundColor: '#fff',
                            border: '1px solid #e5e7eb',
                            overflow: 'hidden',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {product.imageLink ? (
                              <img
                                src={product.imageLink}
                                alt={product.title || 'Produit'}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;color:#9ca3af"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="2" y1="2" x2="22" y2="22"></line><path d="M10.41 10.41a2 2 0 1 1-2.83-2.83"></path><line x1="13.5" y1="13.5" x2="6" y2="21"></line><path d="M18 12l3-3"></path><path d="M3 3l3 3"></path><path d="M20.24 20.24L18 18"></path></svg></div>';
                                }}
                              />
                            ) : (
                              <ImageOff size={24} style={{ color: '#9ca3af' }} />
                            )}
                          </div>

                          {/* Infos produit */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <h4 style={{
                              margin: '0 0 8px 0',
                              fontSize: '15px',
                              fontWeight: '600',
                              color: '#111827',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {product.title || 'Titre non disponible'}
                            </h4>

                            <p style={{
                              margin: '0 0 12px 0',
                              fontSize: '12px',
                              color: '#6b7280',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              ID: {offerId}
                            </p>

                            {/* Raisons du rejet - dédupliquées */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {uniqueIssues.map((issue, issueIndex) => (
                                <span
                                  key={issueIndex}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    padding: '4px 10px',
                                    backgroundColor: issue.severity === 'critical' ? '#dc2626' : '#ef4444',
                                    color: 'white',
                                    fontSize: '12px',
                                    borderRadius: '6px',
                                    fontWeight: '500'
                                  }}
                                  title={issue.description || issue.resolution}
                                >
                                  <AlertTriangle size={12} />
                                  {issue.messageFr}
                                </span>
                              ))}
                            </div>
                          </div>

                          {/* Lien vers Google Merchant Center */}
                          <a
                            href={merchantCenterUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '40px',
                              height: '40px',
                              borderRadius: '8px',
                              backgroundColor: 'white',
                              border: '1px solid #e5e7eb',
                              color: '#6366f1',
                              flexShrink: 0
                            }}
                            title="Voir dans Google Merchant Center"
                          >
                            <ExternalLink size={18} />
                          </a>
                        </div>
                      </div>
                    );
                  })}

                  {/* Bouton charger plus */}
                  {hasMoreRejected && (
                    <button
                      onClick={loadMoreRejected}
                      disabled={loadingRejected}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: '#f3f4f6',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: loadingRejected ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginTop: '8px'
                      }}
                    >
                      {loadingRejected ? 'Chargement...' : `Charger plus (${rejectedProducts.length}/${rejectedTotal})`}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedDiagnosticsTool;
